import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { DiscardChangesDialog } from "./DiscardChangesDialog";

type ConfirmDiscard = () => Promise<boolean>;

const DiscardGuardContext = createContext<ConfirmDiscard | null>(null);

/**
 * One discard confirmation for the whole app. Every caller that is about to drop unsaved changes — the Object Page's
 * Cancel, the router's navigation blocker, closing a detail column — asks through `useDiscardConfirmation()`. While a
 * question is open, further requests share it, so the user never sees two stacked "discard changes?" dialogs.
 */
export function DiscardGuardProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pending = useRef<{ promise: Promise<boolean>; resolve: (discard: boolean) => void } | null>(null);

  const confirm = useCallback<ConfirmDiscard>(() => {
    if (pending.current) return pending.current.promise;
    let resolve: (discard: boolean) => void = () => undefined;
    const promise = new Promise<boolean>((done) => {
      resolve = done;
    });
    pending.current = { promise, resolve };
    setOpen(true);
    return promise;
  }, []);

  const answer = useCallback((discard: boolean) => {
    const current = pending.current;
    pending.current = null;
    setOpen(false);
    current?.resolve(discard);
  }, []);

  const value = useMemo(() => confirm, [confirm]);
  return (
    <DiscardGuardContext.Provider value={value}>
      {children}
      <DiscardChangesDialog
        open={open}
        onDiscard={() => {
          answer(true);
        }}
        onKeepEditing={() => {
          answer(false);
        }}
      />
    </DiscardGuardContext.Provider>
  );
}

/** Asks the user whether unsaved changes may be discarded; resolves `true` to discard. */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useDiscardConfirmation(): ConfirmDiscard {
  const confirm = useContext(DiscardGuardContext);
  if (!confirm) throw new Error("useDiscardConfirmation must be used inside DiscardGuardProvider");
  return confirm;
}
